import { Mail } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";
import { USERS_TABS } from "../constants/tabs";
import {
  USERS_HELPER_CLS,
  USERS_HERO_NAME_CLS,
  USERS_TAB_CLS,
} from "../constants/typography";
import { useUsersContext, userIsDeactivated, userRoleName } from "../context/UsersContext";
import type { TabId } from "../types";
import { RoleBadge } from "./users-ui";
import { UserAvatar } from "@/modern/components/user-avatar";
import { UsersSidebar } from "./UsersSidebar";
import { TooltipProvider } from "@/modern/components/ui/tooltip";
import { OverviewTab } from "./tabs/OverviewTab";
import { TasksTab } from "./tabs/TasksTab";
import { PointsTab } from "./tabs/PointsTab";
import { ContentTab } from "./tabs/ContentTab";
import { OwnershipTab } from "./tabs/OwnershipTab";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { SERVER_BASE_URL } from "@/env";
import { getDeactivatedRole, showConfirmationDialog } from "@/other/utils/utils";
import { MessageDialog } from "@bryntum/schedulerpro";

const outlineBtnCls = "h-8 gap-1.5 px-3 bg-transparent border-border/70 hover:bg-accent font-normal text-sm";
const deleteBtnCls = "h-8 gap-1.5 px-3 font-normal text-sm";

export function UsersSurfaceCard() {
  const { selectedUser, activeTab, setActiveTab, tabScrollRef, project } = useUsersContext();
  const { editorHandler, user: currentUser, selectedBoard } = useSwivuBryntumContext();
  const roleName = userRoleName(selectedUser);
  const isDeactivated = userIsDeactivated(selectedUser);

  function openEditor(e: React.MouseEvent) {
    if (!selectedUser) return;
    if (e.shiftKey) {
      window.open(`${SERVER_BASE_URL}/superuser-page/?email=${selectedUser.email}`, "_blank");
      return;
    }
    editorHandler.openEditor(EDITOR_KEYS.userEditor, selectedUser as any, { user: selectedUser });
  }

  function blockedSelfOrOwner(action: "deactivate" | "delete"): boolean {
    if (!selectedUser) return true;

    if ((currentUser as any)?.id === selectedUser.userId) {
      const verb = action === "delete" ? "delete" : "deactivate";
      MessageDialog.alert({
        title: action === "delete" ? "Cannot Delete User" : "Cannot Deactivate User",
        message: `You cannot ${verb} your own user!`,
      });
      return true;
    }

    if (selectedUser.id === (selectedBoard as any)?.record?.owner) {
      MessageDialog.alert({
        title: action === "delete" ? "Cannot Delete User" : "Cannot Deactivate User",
        message: "You cannot delete or deactivate the board owner!",
      });
      return true;
    }

    return false;
  }

  async function handleDeactivate() {
    if (!selectedUser || !project) return;
    if (blockedSelfOrOwner("deactivate")) return;

    const roleId = getDeactivatedRole((project as any).swivuAppRoles)?.id;
    if (!roleId) return;

    const confirmed = await showConfirmationDialog({
      title: "Deactivate User",
      message: `<div>Are you sure you want to deactivate the following user?<br/>Deactivating users preserves historical data.<br/><br/><b>${selectedUser.name}</b></div>`,
      cancelBtnText: "Cancel",
      confirmBtnText: { cls: "b-raised", color: "b-blue", text: "Deactivate" },
      isCloseByHeader: true,
    } as any);

    if (confirmed) (selectedUser as any).roles = roleId;
  }

  async function handleDelete() {
    if (!selectedUser || !project) return;
    if (blockedSelfOrOwner("delete")) return;

    const confirmed = await showConfirmationDialog({
      title: "Delete User",
      message: `<div>Are you sure you want to delete the following user, including historical data?<br/>This action cannot be undone.<br/><br/><b>${selectedUser.name}</b></div>`,
      cancelBtnText: "Cancel",
      confirmBtnText: { cls: "b-raised bg-destructive text-destructive-foreground hover:bg-destructive/90", text: "Delete" },
      isCloseByHeader: true,
    } as any);

    if (confirmed) project.boardUsersStore.remove(selectedUser as any);
  }

  if (!selectedUser) {
    return (
      <TooltipProvider delayDuration={150}>
        <div className="flex-1 min-h-0 flex flex-col border-y border-border bg-background overflow-hidden">
          <div className="flex-1 min-h-0 grid grid-cols-12 overflow-hidden">
            <UsersSidebar />
            <main className="relative z-0 col-span-12 lg:col-span-8 xl:col-span-9 flex flex-col min-h-0 items-center justify-center bg-background">
              <p className={USERS_HELPER_CLS}>Loading users…</p>
            </main>
          </div>
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex-1 min-h-0 flex flex-col border-y border-border bg-background overflow-hidden">
        <div className="flex-1 min-h-0 grid grid-cols-12 overflow-hidden">
          <UsersSidebar />

          <main className="relative z-0 col-span-12 lg:col-span-8 xl:col-span-9 flex flex-col min-h-0 min-w-0 bg-background overflow-hidden">
            <header className="shrink-0 px-6 pt-6 pb-5 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4 min-w-0">
                <UserAvatar
                  user={selectedUser as any}
                  size="large"
                  avatarClassName="h-11 w-11 text-base"
                  className="shrink-0"
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h2 className={cn(USERS_HERO_NAME_CLS, "truncate")}>
                      {selectedUser.name}
                    </h2>
                  </div>
                  <div className="mt-2.5 flex items-center gap-2.5 flex-wrap">
                    {roleName ? <RoleBadge role={roleName} isDeactivated={isDeactivated} /> : null}
                    {selectedUser.email && (
                      <>
                        {roleName ? <span className="text-border-enhanced">·</span> : null}
                        <span className="text-base text-muted-foreground flex items-center gap-1.5 truncate">
                          <Mail className="size-4 shrink-0" />
                          {selectedUser.email}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openEditor}
                  className={outlineBtnCls}
                >
                  <i className="fa-light fa-pen text-sm" aria-hidden />
                  Edit
                </Button>
                {isDeactivated ? (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDelete}
                    className={deleteBtnCls}
                  >
                    <i className="fa-light fa-trash text-sm" aria-hidden />
                    Delete
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeactivate}
                    className={outlineBtnCls}
                  >
                    <i className="fa-light fa-user-slash text-sm" aria-hidden />
                    Deactivate
                  </Button>
                )}
              </div>
            </header>

            <nav className="shrink-0 px-6 pt-1 border-b border-border/70">
              <div className="flex gap-5 overflow-x-auto overflow-y-hidden">
                {USERS_TABS.map((t) => {
                  const on = t.id === activeTab;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setActiveTab(t.id as TabId)}
                      className={cn(
                        "inline-flex items-center gap-1.5 pb-2.5 pt-0.5 transition-colors border-b-2 -mb-px whitespace-nowrap",
                        USERS_TAB_CLS,
                        on
                          ? "text-foreground border-primary"
                          : "text-muted-foreground hover:text-foreground border-transparent",
                      )}
                    >
                      <i className={cn(t.icon, "shrink-0 opacity-80 text-base")} aria-hidden />
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </nav>

            <div
              ref={(el) => { tabScrollRef.current = el; }}
              className="flex-1 min-h-0 overflow-y-auto p-6"
            >
              {activeTab === "overview" && <OverviewTab user={selectedUser} />}
              {activeTab === "tasks" && <TasksTab user={selectedUser} />}
              {activeTab === "points" && <PointsTab user={selectedUser} />}
              {activeTab === "content" && <ContentTab user={selectedUser} />}
              {activeTab === "ownership" && <OwnershipTab user={selectedUser} />}
            </div>
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
